import momentTz from 'moment-timezone';

import ApiUtils from '@/api/util/ApiUtils';
import AddressUtils from '@/api/util/AddressUtils';
import EventModel from '@/api/model/EventModel';
import TypeUtils from '../util/TypeUtils';
import {OrderFragment_ticketDefinitions} from '@/api/gql/gql-types/OrderFragment';

export default class EventDTO {

  // this is not actually returning a 'Preview' but it's also not returning a clean Event object - keep bad name
  public static toPreview(obj: EventModel, tickets: OrderFragment_ticketDefinitions[]) {

    const prices: number[] = [];
    const addressString = !ApiUtils.hasValue(obj.venueAddress) ? '' :
      AddressUtils.toString(obj.venueAddress.street, obj.venueAddress.street2, obj.venueAddress.city,
      obj.venueAddress.state, obj.venueAddress.zip, obj.venueAddress.country);

    for (const ticket of tickets) {
      prices.push(ticket.price === null ||  ticket.price === '' ? 0 : Number(ticket.price));
    }
    prices.sort();
    return {
      actual: obj,
      id: obj.id,
      address: addressString,
      streetAddress: obj.venueAddress.street,
      streetAddress2: obj.venueAddress.street2,
      city: obj.venueAddress.city,
      state: obj.venueAddress.state,
      zip: obj.venueAddress.zip,
      country: obj.venueAddress.country,
      name: obj.name, venueName: obj.venueName,
      eventDate: obj.eventStart,
      eventEnd: obj.eventEnd,
      eventTimezone: obj.eventTimezone,
      posterUrl: obj.posterUrl, posterMode: obj.posterMode,
      coverImageUrl: obj.coverImageUrl,
      mainLogoImageUrl: obj.mainLogoImageUrl,
      enableBranding: ApiUtils.hasValue(obj.mainLogoImageUrl),
      enableCheckoutDonation: TypeUtils.toBoolean(obj.enableCheckoutDonation),
      enableBuyerClaimAll: TypeUtils.toBoolean(obj.enableBuyerClaimAll),
      enableTax: TypeUtils.toBoolean(obj.enableTax),
      enablePublicGuestList: TypeUtils.toBoolean(obj.enablePublicGuestList),
      terms: ApiUtils.nullToEmpty(obj.terms),
      description: ApiUtils.nullToEmpty(obj.description),
      summary: ApiUtils.nullToEmpty(obj.summary),
      buttonTextColor: obj.buttonTextColor,
      footerBgColor: obj.footerBgColor,
      buttonText: obj.buttonText,
      buttonBgColor: obj.buttonBgColor,
      footerLogoImageUrl: obj.footerLogoImageUrl,
      taxRate: obj.taxRate,
      embedId: obj.embedId,
      embed: obj.embed,
      guestDetailsSettings: EventDTO.toGuestDetails(obj.guestDetailsSettings),
      ticketEmbedSettings: obj.ticketEmbedSettings,
      templateVersion: obj.templateVersion,
      googleAnalyticsKey: obj.googleAnalyticsKey,
      hasActivePromo: obj.hasActivePromo,

      // Computed
      min: Math.min(...prices), max: Math.max(...prices),
      organization: EventDTO.toOrganization(obj.organization),
      currencyCode: ApiUtils.toStringPath(obj, 'organization.currencyCode', 'USD'),
    };
  }

  public static isEventInactive(obj: EventModel) {
    return obj.status === 'ARCHIVE' || momentTz.utc(obj.eventEnd) < momentTz.utc(Date.now());
  }

  public static toTicketEmbedSettings(obj: any, queryParams: any) {
    if (obj === null) { return {}; }

    if (queryParams.showEventTime) {
      obj.showEventTime = queryParams.showEventTime === 'true';
    }
    if (queryParams.showEventImage) {
      obj.showEventImage = queryParams.showEventImage === 'true';
    }
    if (queryParams.showEventLocation) {
      obj.showEventLocation = queryParams.showEventLocation === 'true';
    }
    if (queryParams.showEventDescription) {
      obj.showEventDescription = queryParams.showEventDescription === 'true';
    }
    if (queryParams.showEventBanner) {
      obj.showEventBanner = queryParams.showEventBanner === 'true';
    }
    if (queryParams.theme) {
      obj.theme = queryParams.theme;
    }
    return {
      ...obj,
    };
  }

  private static toOrganization(obj: any) {
    if (obj === null) { return {}; }

    return {
      name: ApiUtils.toStringPath(obj, 'name', ''),
      id: obj.id,
      gateway: obj.defaultPaymentGateway || {},
    };
  }

  private static toGuestDetails(obj: any) {
    // some guestDeatilsSettings should be forced (at least for P1)
    const defaults = {
        firstName: true,
        lastName: true,
        email: true,
    };
    if (obj === null) {
      return {...defaults};
    }

    return {
      ...obj,
      ...defaults,
      salutationRequired: obj.salutationRequired || false,
      phoneRequired: obj.phoneRequired || false,
      address1Required: obj.address1Required || false,
      address2Required: obj.address2Required || false,
      cityRequired: obj.cityRequired || false,
      stateRequired: obj.stateRequired || false,
      zipRequired: obj.zipRequired || false,
      companyNameRequired: obj.companyNameRequired || false,
      countryRequired: obj.countryRequired || false,
    };
  }
}
